/**
 * Headless Voyado landing-page primary list.
 *
 * The raw response is exposed as `state.response`; rendering is handled by
 * voyado-landing-page.js. Query state is mirrored to the browser URL.
 */
(function initializeVoyadoLandingPageState(window) {
    'use strict';

    if (!window.VOYADO_CONTEXT?.enabled || !window.VOYADO_ELEVATE?.createClient) {
        return;
    }

    const EVENT_NAME = 'voyado:landing-page-state';
    const DEFAULT_PAGE_SIZE = 24;
    const client = window.VOYADO_ELEVATE.createClient();
    const context = window.VOYADO_CONTEXT?.landingPage ?? {};
    const pageReference = String(context.pageReference ?? '').trim();
    const pageSize = Math.max(1, Number(context.pageSize) || DEFAULT_PAGE_SIZE);
    const configuredProductCount = Number(context.productCount);
    const productCount =
        Number.isFinite(configuredProductCount) && configuredProductCount >= 0
            ? configuredProductCount
            : null;

    if (!pageReference) return;

    let requestController = null;
    let requestId = 0;
    let state = createInitialState();

    function clone(value) {
        if (typeof structuredClone === 'function') return structuredClone(value);
        return JSON.parse(JSON.stringify(value));
    }

    function deepFreeze(value) {
        if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value;
        Object.freeze(value);
        Object.values(value).forEach(deepFreeze);
        return value;
    }

    function freeze(value) {
        return deepFreeze(clone(value));
    }

    function readFilters(parameters) {
        const filters = {};

        for (const [key, value] of parameters.entries()) {
            if (!key.startsWith('f.')) continue;
            const filterKey = key.slice(2);

            if (filterKey.endsWith('.min') || filterKey.endsWith('.max')) {
                const bound = filterKey.endsWith('.min') ? 'min' : 'max';
                const id = filterKey.slice(0, -(bound.length + 1));
                const number = Number(value);
                if (Number.isFinite(number)) {
                    filters[id] = { ...(filters[id] ?? {}), [bound]: number };
                }
            } else {
                filters[filterKey] = value.split('|').filter(Boolean);
            }
        }

        return filters;
    }

    function createInitialState() {
        const url = new URL(window.location.href);
        const page = Math.max(1, Math.floor(Number(url.searchParams.get('page')) || 1));

        return freeze({
            status: 'idle',
            error: null,
            request: {
                pageReference,
                query: String(url.searchParams.get('q') ?? '').trim(),
                sort: String(url.searchParams.get('sort') ?? '').trim() || null,
                filters: readFilters(url.searchParams),
                skip: (page - 1) * pageSize,
                limit: pageSize,
            },
            response: null,
            loadedProductGroups: [],
            hasMore: false,
            page,
        });
    }

    function setState(update) {
        const patch = typeof update === 'function' ? update(state) : update;
        state = freeze({ ...state, ...patch });
        window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: state }));
        return state;
    }

    function subscribe(listener) {
        const handleChange = (event) => listener(event.detail);
        window.addEventListener(EVENT_NAME, handleChange);
        listener(state);
        return () => window.removeEventListener(EVENT_NAME, handleChange);
    }

    function applyFilters(parameters, filters) {
        for (const [id, value] of Object.entries(filters ?? {})) {
            if (Array.isArray(value)) {
                if (value.length) parameters[`f.${id}`] = value;
                continue;
            }
            if (!value || typeof value !== 'object') continue;
            if (value.min !== undefined && value.min !== null) {
                parameters[`f.${id}.min`] = value.min;
            }
            if (value.max !== undefined && value.max !== null) {
                parameters[`f.${id}.max`] = value.max;
            }
        }
        return parameters;
    }

    function requestParameters() {
        const parameters = {
            pageReference: state.request.pageReference,
            skip: state.request.skip,
            limit: state.request.limit,
        };

        if (state.request.query) parameters.q = state.request.query;
        if (state.request.sort) parameters.sort = state.request.sort;
        return applyFilters(parameters, state.request.filters);
    }

    function pageConfiguration() {
        const configuration = { primaryList: { include: true } };
        if (state.response?.listToken) configuration.listToken = state.response.listToken;
        return configuration;
    }

    function startRequest() {
        requestController?.abort();
        requestController = new AbortController();
        requestId += 1;
        return { id: requestId, signal: requestController.signal };
    }

    function shouldUseShopifyFallback(response) {
        const totalHits = Number(response?.primaryList?.totalHits);
        if (!Number.isFinite(totalHits)) return true;
        return productCount !== null && totalHits > productCount;
    }

    async function fetchPage({ append }) {
        if (state.status === 'loading' || (append && !state.hasMore)) return state;

        const activeRequest = startRequest();
        const requestedSkip = state.request.skip;
        setState({ status: 'loading', error: null });

        try {
            const response = await client.landingPage(
                requestParameters(),
                pageConfiguration(),
                { signal: activeRequest.signal }
            );
            if (activeRequest.id !== requestId) return state;

            if (window.VOYADO_CONTEXT?.debug === true) {
                console.log('[Voyado landing page] Full response', response);
            }

            if (shouldUseShopifyFallback(response)) {
                if (window.VOYADO_CONTEXT?.debug === true) {
                    console.warn(
                        '[Voyado landing page] The result exceeded the Shopify collection boundary; using Shopify collection results.',
                        {
                            pageReference,
                            shopifyProductCount: productCount,
                            voyadoTotalHits: response?.primaryList?.totalHits ?? null,
                        }
                    );
                }

                return setState({
                    status: 'fallback',
                    error: null,
                    response,
                    loadedProductGroups: [],
                    hasMore: false,
                    page: 1,
                });
            }

            const pageGroups = Array.isArray(response?.primaryList?.productGroups)
                ? response.primaryList.productGroups
                : [];
            const groups = append
                ? [...state.loadedProductGroups, ...pageGroups]
                : pageGroups;
            const totalHits = Number(response?.primaryList?.totalHits) || 0;
            const nextSkip = requestedSkip + pageGroups.length;

            return setState({
                status: 'success',
                error: null,
                response,
                loadedProductGroups: groups,
                hasMore: pageGroups.length > 0 && nextSkip < totalHits,
                page: Math.floor(requestedSkip / state.request.limit) + 1,
                request: { ...state.request, skip: nextSkip },
            });
        } catch (error) {
            if (activeRequest.id !== requestId) return state;
            return setState({
                status: 'error',
                error: {
                    name: error?.name ?? 'Error',
                    message: error?.message ?? 'Could not load the Voyado landing page.',
                    status: error?.status ?? null,
                    response: error?.response ?? null,
                },
            });
        }
    }

    function writeUrl() {
        const url = new URL(window.location.href);
        const request = state.request;

        for (const key of [...url.searchParams.keys()]) {
            if (key === 'page' || key === 'sort' || key === 'q' || key.startsWith('f.')) {
                url.searchParams.delete(key);
            }
        }

        if (request.query) url.searchParams.set('q', request.query);
        if (request.sort) url.searchParams.set('sort', request.sort);
        if (state.page > 1) url.searchParams.set('page', String(state.page));

        for (const [id, value] of Object.entries(request.filters ?? {})) {
            if (Array.isArray(value)) {
                if (value.length) url.searchParams.set(`f.${id}`, value.join('|'));
            } else if (value && typeof value === 'object') {
                if (value.min !== undefined && value.min !== null) {
                    url.searchParams.set(`f.${id}.min`, String(value.min));
                }
                if (value.max !== undefined && value.max !== null) {
                    url.searchParams.set(`f.${id}.max`, String(value.max));
                }
            }
        }

        window.history.pushState({}, '', url);
    }

    async function resetAndFetch(patch, updateUrl = true) {
        setState({
            status: 'idle',
            error: null,
            request: { ...state.request, ...patch, skip: 0 },
            response: null,
            loadedProductGroups: [],
            hasMore: false,
            page: 1,
        });
        if (updateUrl) writeUrl();
        return fetchPage({ append: false });
    }

    const api = Object.freeze({
        getState: () => state,
        subscribe,
        loadMore() {
            return fetchPage({ append: true }).then((result) => {
                writeUrl();
                return result;
            });
        },
        goToPage(page) {
            const requestedPage = Math.max(1, Math.floor(Number(page) || 1));
            const totalHits = Number(state.response?.primaryList?.totalHits) || 0;
            const totalPages = Math.max(1, Math.ceil(totalHits / state.request.limit));
            const targetPage = Math.min(requestedPage, totalPages);
            setState({
                status: 'idle',
                error: null,
                request: {
                    ...state.request,
                    skip: (targetPage - 1) * state.request.limit,
                },
                loadedProductGroups: [],
                hasMore: false,
                page: targetPage,
            });
            writeUrl();
            return fetchPage({ append: false });
        },
        setSort(sort) {
            return resetAndFetch({ sort: String(sort ?? '').trim() || null });
        },
        setFilters(filters) {
            return resetAndFetch({ filters: filters ?? {} });
        },
        clearFilters() {
            return resetAndFetch({ filters: {} });
        },
        destroy() {
            requestController?.abort();
            requestController = null;
        },
    });

    window.VOYADO_LANDING_PAGE = api;

    window.addEventListener('popstate', () => {
        const url = new URL(window.location.href);
        const page = Math.max(1, Math.floor(Number(url.searchParams.get('page')) || 1));
        setState({
            status: 'idle',
            error: null,
            request: {
                ...state.request,
                query: String(url.searchParams.get('q') ?? '').trim(),
                sort: String(url.searchParams.get('sort') ?? '').trim() || null,
                filters: readFilters(url.searchParams),
                skip: (page - 1) * state.request.limit,
            },
            response: null,
            loadedProductGroups: [],
            hasMore: false,
            page,
        });
        fetchPage({ append: false });
    });

    fetchPage({ append: false });
})(window);
